import { z } from 'zod';

// matches.search reads from public.matches (already populated by the
// api-tennis connector every 30s). Returning denormalized player +
// tournament names so the UI doesn't have to re-query.

// Tournament level groupings exposed to the UI as a single dropdown filter.
// Maps to several values in the underlying tournaments.level column (which
// reflects api-tennis's enum: 'Atp Singles', 'Wta Singles', 'Challenger Men
// Singles', 'Itf Men Singles', 'Itf Women Singles', 'Wta Doubles', etc.).
// Server-side we resolve each group to the matching values via ILIKE.
export const TOURNAMENT_LEVEL_FILTERS = ['all', 'atp', 'wta', 'challenger', 'itf'] as const;
export const TournamentLevelFilterSchema = z.enum(TOURNAMENT_LEVEL_FILTERS);
export type TournamentLevelFilter = z.infer<typeof TournamentLevelFilterSchema>;

export const MatchesSearchInputSchema = z.object({
  // Free-text fragment matched against player names + tournament name.
  // Empty = no text filter (paginated by limit).
  query: z.string().max(120).optional(),
  // Defaults: now → +48h. The api-tennis connector keeps a 5-7 day forward
  // horizon so operators can also browse upcoming days.
  fromUtc: z.string().datetime().optional(),
  toUtc: z.string().datetime().optional(),
  surface: z.enum(['hard', 'clay', 'grass', 'carpet', 'indoor_hard']).optional(),
  // Optional level grouping. 'all' (or omitted) = no filter. The route maps
  // each value to the corresponding api-tennis tournament-level strings.
  level: TournamentLevelFilterSchema.optional(),
  limit: z.number().int().min(1).max(50).default(25),
});
export type MatchesSearchInput = z.infer<typeof MatchesSearchInputSchema>;

export const MatchSearchResultSchema = z.object({
  id: z.string().uuid(),
  startsAt: z.string().datetime(),
  status: z.string(),
  surface: z.string().nullable(),
  bestOf: z.number().int(),
  // Populated by the connector-api-tennis fixture-sync (every cycle, ~10min).
  // Returns null only when api-tennis omitted first_player_key /
  // second_player_key / tournament_key on a given event — rare. Frontend
  // can safely render names as primary identifiers; falls back to the
  // canonical_match_id only if a key was missing upstream.
  p1Name: z.string().nullable(),
  p2Name: z.string().nullable(),
  tournamentName: z.string().nullable(),
  // tournaments.level — raw api-tennis string (e.g. "Atp Singles",
  // "Itf Women Singles"). Useful for the operator to scan at a glance.
  tournamentLevel: z.string().nullable(),
});
export type MatchSearchResult = z.infer<typeof MatchSearchResultSchema>;
